import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap-v5';
import { SimpleAlert } from '../../../../../rbts/helpers';
import { EventoBoleteraModel } from '../models/EventoBoleteraModel';
import { actualizarEventoBoletera } from '../../../../service/boletera/EventosBoletera';
import { obtenerRecintoSelect, crearRecintoService, uploadMapaEvento } from '../../../../service/recintos/Recintos';

type Props = {
  evento: EventoBoleteraModel;
  onSaved: () => void;
};

const DatosYMapa: React.FC<Props> = ({ evento, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [recintoOpts, setRecintoOpts] = useState<Array<{ value: number; label: string }>>([]);
  const [showNuevoRecinto, setShowNuevoRecinto] = useState(false);
  const [nuevoRecinto, setNuevoRecinto] = useState({ nombre: '', direccion: '', aforo_max: '' });
  const [savingRecinto, setSavingRecinto] = useState(false);
  const [form, setForm] = useState({
    nombre: evento.nombre || '',
    tipo: evento.tipo || '',
    recinto_id: evento.recinto_id ?? '',
    aforo_max_evento: evento.aforo_max_evento ?? '',
    descripcion: evento.descripcion || '',
    mapa_ubicaciones: evento.mapa_ubicaciones || '',
  });

  useEffect(() => {
    obtenerRecintoSelect().then(setRecintoOpts).catch(() => {});
  }, []);

  useEffect(() => {
    setForm({
      nombre: evento.nombre || '',
      tipo: evento.tipo || '',
      recinto_id: evento.recinto_id ?? '',
      aforo_max_evento: evento.aforo_max_evento ?? '',
      descripcion: evento.descripcion || '',
      mapa_ubicaciones: evento.mapa_ubicaciones || '',
    });
  }, [evento]);

  const handleMapaUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.match(/image\/(jpeg|png)/)) {
      SimpleAlert('Solo se permiten imágenes JPG o PNG.', 3000, 'error');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const response = await uploadMapaEvento(file);
      const url = response?.doc?.data?.imageUrl || response?.data?.imageUrl;
      if (url) {
        setForm((prev) => ({ ...prev, mapa_ubicaciones: url }));
        await actualizarEventoBoletera(evento.id, { mapa_ubicaciones: url });
        onSaved();
        SimpleAlert('Mapa subido y guardado', 2000, 'success');
      }
    } catch {
      SimpleAlert('Error al subir el mapa. Intenta de nuevo.', 3000, 'error');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleCrearRecinto = async () => {
    if (!nuevoRecinto.nombre.trim()) { SimpleAlert('El nombre es requerido.', 3000, 'error'); return; }
    if (!nuevoRecinto.aforo_max || Number(nuevoRecinto.aforo_max) <= 0) { SimpleAlert('El aforo máximo es requerido.', 3000, 'error'); return; }
    setSavingRecinto(true);
    try {
      const resp = await crearRecintoService({ nombre: nuevoRecinto.nombre, direccion: nuevoRecinto.direccion, aforo_max: Number(nuevoRecinto.aforo_max) });
      const newId = resp?.data?.doc?.data?.id;
      const opts = await obtenerRecintoSelect();
      setRecintoOpts(opts);
      if (newId) setForm((p) => ({ ...p, recinto_id: newId }));
      setShowNuevoRecinto(false);
      setNuevoRecinto({ nombre: '', direccion: '', aforo_max: '' });
      SimpleAlert('Recinto creado y seleccionado', 2500, 'success');
    } catch (error: any) {
      SimpleAlert(error?.response?.data?.message || 'Error al crear el recinto.', 3000, 'error');
    } finally {
      setSavingRecinto(false);
    }
  };

  const handleSave = async () => {
    if (!form.nombre.trim()) { SimpleAlert('El nombre es requerido.', 3000, 'error'); return; }
    setSaving(true);
    try {
      await actualizarEventoBoletera(evento.id, {
        nombre: form.nombre,
        tipo: form.tipo || undefined,
        recinto_id: form.recinto_id ? Number(form.recinto_id) : null,
        aforo_max_evento: form.aforo_max_evento ? Number(form.aforo_max_evento) : undefined,
        descripcion: form.descripcion,
        mapa_ubicaciones: form.mapa_ubicaciones || undefined,
      });
      SimpleAlert('Cambios guardados', 2500, 'success');
      onSaved();
    } catch (error: any) {
      SimpleAlert(error?.response?.data?.message || 'Error al guardar.', 3000, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <div className="row">
      {/* ── Columna izquierda ── */}
      <div className="col-lg-7">
        <div className="mb-5">
          <label className="required fw-bold fs-6 mb-2">Nombre del evento</label>
          <input className="form-control" value={form.nombre} onChange={(e) => setForm((p) => ({ ...p, nombre: e.target.value }))} />
        </div>
        <div className="mb-5">
          <label className="fw-bold fs-6 mb-2">Tipo</label>
          <input className="form-control" placeholder="Concierto, Congreso, Teatro…" value={form.tipo} onChange={(e) => setForm((p) => ({ ...p, tipo: e.target.value }))} />
        </div>
        <div className="mb-5">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label className="fw-bold fs-6">Recinto</label>
            <button type="button" className="btn btn-link btn-sm p-0 text-primary" onClick={() => setShowNuevoRecinto(true)}>
              + Nuevo recinto
            </button>
          </div>
          <select
            className="form-select"
            value={form.recinto_id}
            onChange={(e) => setForm((p) => ({ ...p, recinto_id: e.target.value ? Number(e.target.value) : '' }))}
          >
            <option value="">— Sin recinto —</option>
            {recintoOpts.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
        <div className="mb-5">
          <label className="required fw-bold fs-6 mb-2">Aforo máximo del evento</label>
          <input
            className="form-control"
            type="number"
            min={1}
            value={form.aforo_max_evento}
            onChange={(e) => setForm((p) => ({ ...p, aforo_max_evento: e.target.value }))}
          />
          {evento.recinto?.aforo_max && (
            <div className="text-muted fs-7 mt-1">
              Aforo del recinto: {Number(evento.recinto.aforo_max).toLocaleString()}
            </div>
          )}
        </div>
        <div className="mb-5">
          <label className="fw-bold fs-6 mb-2">Descripción</label>
          <textarea
            className="form-control"
            rows={3}
            value={form.descripcion}
            onChange={(e) => setForm((p) => ({ ...p, descripcion: e.target.value }))}
          />
        </div>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? <><span className="spinner-border spinner-border-sm me-2" />Guardando…</> : 'Guardar cambios'}
        </button>
      </div>

      {/* ── Columna derecha: mapa ── */}
      <div className="col-lg-5">
        <label className="fw-bold fs-6 mb-3">Mapa de ubicaciones (imagen de referencia)</label>
        {form.mapa_ubicaciones ? (
          <div className="mb-3 text-center">
            <img
              src={form.mapa_ubicaciones}
              alt="Mapa de ubicaciones"
              className="img-fluid rounded border"
              style={{ maxHeight: 280, objectFit: 'contain' }}
            />
            <div className="text-success fs-7 mt-1">
              ✓ Mapa cargado ({form.mapa_ubicaciones.split('/').pop()})
            </div>
          </div>
        ) : (
          <div className="mb-3 p-5 border border-dashed rounded text-center text-muted">
            Sin mapa de ubicaciones
          </div>
        )}
        <div>
          <label className="fw-bold fs-7 mb-1">Cambiar imagen (jpg/png, máx 5 MB)</label>
          <input
            className="form-control form-control-sm"
            type="file"
            accept="image/jpeg,image/png"
            onChange={handleMapaUpload}
            disabled={uploading}
          />
          {uploading && <div className="text-muted fs-7 mt-1">Subiendo imagen…</div>}
        </div>
      </div>
    </div>

      {/* ── Mini-modal: Nuevo recinto ── */}
      <Modal
        backdrop="static"
        dialogClassName="modal-dialog mw-500px"
        show={showNuevoRecinto}
        onHide={() => { setShowNuevoRecinto(false); setNuevoRecinto({ nombre: '', direccion: '', aforo_max: '' }); }}
      >
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-muted">Nuevo recinto</h2>
            <button type="button" className="btn btn-sm btn-icon btn-active-color-primary" onClick={() => { setShowNuevoRecinto(false); setNuevoRecinto({ nombre: '', direccion: '', aforo_max: '' }); }}>✕</button>
          </div>
          <div className="modal-body mx-5">
            <div className="mb-4">
              <label className="required fw-bold fs-6 mb-2">Nombre</label>
              <input className="form-control" placeholder="Ej: Cintermex" maxLength={100} value={nuevoRecinto.nombre} onChange={(e) => setNuevoRecinto((p) => ({ ...p, nombre: e.target.value }))} />
            </div>
            <div className="mb-4">
              <label className="fw-bold fs-6 mb-2">Dirección</label>
              <input className="form-control" placeholder="Ej: Av. Fundidora 501, Monterrey" maxLength={255} value={nuevoRecinto.direccion} onChange={(e) => setNuevoRecinto((p) => ({ ...p, direccion: e.target.value }))} />
            </div>
            <div className="mb-4">
              <label className="required fw-bold fs-6 mb-2">Aforo máximo (personas)</label>
              <input className="form-control" type="number" min={1} placeholder="Ej: 8000" value={nuevoRecinto.aforo_max} onChange={(e) => setNuevoRecinto((p) => ({ ...p, aforo_max: e.target.value }))} />
            </div>
            <div className="d-flex justify-content-end mt-6">
              <button type="button" className="btn btn-light me-3" onClick={() => { setShowNuevoRecinto(false); setNuevoRecinto({ nombre: '', direccion: '', aforo_max: '' }); }}>Cancelar</button>
              <button type="button" className="btn btn-primary" onClick={handleCrearRecinto} disabled={savingRecinto}>
                {savingRecinto ? <><span className="spinner-border spinner-border-sm me-2" />Guardando…</> : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export { DatosYMapa };
